import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { ErrorPage } from "@/components/error-page/ErrorPage";
import { GridPageLayout } from "@/components/general/grid-page-layout/GridPageLayout";
import { NavButtons } from "@/components/general/nav-buttons/NavButtons";
import { Section } from "@/components/general/section";
import { TransactionStatus } from "@/components/transaction/transaction-status/TransactionStatus";
import { TransactionStatusSkeleton } from "@/components/transaction/transaction-status/transaction-status-skeleton/TransactionStatusSkeleton";
import { useEmailVerification } from "@/hooks/withdrawals/useEmailVerification";
import { useWithdrawalStatus } from "@/hooks/withdrawals/useWithdrawalStatus";
import { GLOBAL_NAVIGATION_URLS, globalNavigation } from "@/utils/constants";
import { ECashierFlows, ERoutes } from "@/utils/enums";
import { useTradingAccounts } from "@/hooks/useTradingAccounts";
import { useAccount } from "@/hooks/deposit/useAccount";
import { EnumCashierTxStatus } from "@/api/cashier";

export const WithdrawalCompletion = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: withdrawalRequestId } = useParams();

  const searchParams = new URLSearchParams(location.search);
  const verificationParam = searchParams.get("verification");
  const statusParam = searchParams.get("status");
  const hasTimeout = searchParams.get("timeout") === "true";
  const accountId = searchParams.get("accountId");
  const emailVerificationToken =
    searchParams.get("emailVerificationToken") ?? undefined;

  const shouldCancel = statusParam === "cancelled" && !hasTimeout; // Don't cancel if timeout

  const isEmailConfirm =
    verificationParam === "email" && !shouldCancel && !hasTimeout;

  const {
    data: accounts,
    isLoading: accountsLoading,
    isFetching: isAccountsFetching,
    refetch: refetchAccounts,
  } = useTradingAccounts();
  const [pendingToAccounts, setPendingToAccounts] = useState(false);
  const { selectedAccount } = useAccount();
  const selectedTradingAccount =
    accounts?.find((acc) => acc.id === accountId) || selectedAccount;

  const {
    verificationResult,
    cancelResult,
    isLoading: emailVerifyLoading,
    error: emailVerifyError,
    isCancelling,
    cancelError,
  } = useEmailVerification({
    withdrawalRequestId,
    emailVerificationToken,
    enabled:
      isEmailConfirm && !!withdrawalRequestId && !!emailVerificationToken,
    shouldCancel: shouldCancel && !!withdrawalRequestId,
  });

  const { status, account, isCancelled } = useWithdrawalStatus({
    verificationParam,
    statusParam,
    verificationResult,
    cancelResult,
    selectedAccount: selectedTradingAccount,
    locationState: location.state,
  });

  // Eagerly refetch trading accounts when a withdrawal completes so the updated
  // balance is ready before the user navigates back to the account selector.
  useEffect(() => {
    if (status?.status !== EnumCashierTxStatus.CashierTxComplete) return;
    refetchAccounts();
  }, [status?.status, refetchAccounts]);

  // Once the user has requested navigation to accounts, wait for the in-flight
  // refetch to finish before actually navigating.
  useEffect(() => {
    if (!pendingToAccounts || isAccountsFetching) return;
    navigate(`/${ECashierFlows.Withdraw}`);
  }, [pendingToAccounts, isAccountsFetching, navigate]);

  const loading = emailVerifyLoading || isCancelling || accountsLoading;

  if (!loading && !account) {
    return <Navigate to={`../${ERoutes.ChooseMethod}`} />;
  }

  if (
    (emailVerifyError && verificationParam === "email") ||
    cancelError ||
    (!loading && (!status || !account))
  ) {
    return (
      <ErrorPage buttonAction={() => navigate(`/${ECashierFlows.Withdraw}`)} />
    );
  }

  return (
    <GridPageLayout>
      <Section>
        {!loading && account && status ? (
          <TransactionStatus
            account={account}
            transactionType={"withdrawal"}
            transactionStatus={status}
          />
        ) : (
          <TransactionStatusSkeleton />
        )}
        <NavButtons
          nextLabel={
            isCancelled || hasTimeout
              ? t("withdrawals.verification.make_another_transaction")
              : t("global.nav_buttons.view_history")
          }
          nextIsLoading={
            (isCancelled || hasTimeout) &&
            pendingToAccounts &&
            isAccountsFetching
          }
          nextAction={
            isCancelled || hasTimeout
              ? () => setPendingToAccounts(true)
              : () => navigate(`/${ECashierFlows.History}`)
          }
          backLabel={t("global.nav_buttons.back_to_dashboard")}
          backAction={() => globalNavigation(GLOBAL_NAVIGATION_URLS.DASHBOARD)}
        />
      </Section>
    </GridPageLayout>
  );
};
